import React, { useEffect, useState } from "react";
import { Dropdown, Drawer, Collapse } from "antd";
import type { MenuProps } from "antd";
import Icon from "@mdi/react";
import { mdiMenuDown, mdiMenu, mdiClose, mdiLogout } from "@mdi/js";
import { Link } from "react-router-dom";
import { useAuth } from "../../../services/AuthContext";
import UserApi from "../../../api-services/user-api.service";
import { SIGNIN_URL } from "../../../utils/helpers";
import { NAV_ITEMS } from "./mock";

const Header: React.FC<{ serverSideName: string; isPartner: boolean }> = ({ serverSideName, isPartner }) => {
  const { username, logout, updateUsername, clearUser } = useAuth();
  const displayUsername = isPartner ? serverSideName : username;
  const [menuOpen, setMenuOpen] = useState(false);
  const truncateLength = 12;

  // Mirror the v1 header auth logic: partners are server-side only (drop any
  // cached customer), while regular users seed the client store from the
  // server-provided name.
  useEffect(() => {
    if (isPartner) {
      localStorage.removeItem("currentUser");
      return;
    }

    if (serverSideName) updateUsername(serverSideName);
  }, [serverSideName, isPartner]);

  const partnerLogout = () => {
    UserApi.logout().then(() => clearUser());
  };

  // Signed-in dropdown menu, matching v1: partners only get Logout; regular
  // users also get Profile and Bookings.
  const truncatedName =
    displayUsername && displayUsername.length > truncateLength
      ? displayUsername.substring(0, truncateLength) + "..."
      : displayUsername || "";

  const userMenu: MenuProps["items"] = isPartner
    ? [
        {
          key: "logout",
          label: (
            <span onClick={partnerLogout}>
              <Icon path={mdiLogout} size="16px" /> Logout
            </span>
          ),
        },
      ]
    : [
        { key: "profile", label: <Link to="/web/profile">Profile</Link> },
        { key: "bookings", label: <Link to="/web/bookings">Bookings</Link> },
        {
          key: "logout",
          label: (
            <span onClick={logout}>
              <Icon path={mdiLogout} size="16px" /> Logout
            </span>
          ),
        },
      ];

  // Mobile drawer: each top-level nav item becomes a collapsible panel listing
  // its children, so the same NAV_ITEMS drives both desktop dropdowns and mobile.
  const drawerItems = NAV_ITEMS.map((item) => ({
    key: item.label,
    label: item.label,
    children: (
      <div className="gd-header-v2__drawer-links">
        {(item.children ?? []).map((c) => (
          <Link key={c.href + c.label} to={c.href} onClick={() => setMenuOpen(false)}>
            {c.label}
          </Link>
        ))}
      </div>
    ),
  }));

  return (
    <header id="gd-header-v2">
      <div className="gd-header-v2__inner">
        <span className="gd-header-v2__swoosh" aria-hidden="true">
          <svg viewBox="0 0 330 83" preserveAspectRatio="none">
            <path d="M0,0 L285,0 C285,48 255,83 195,83 L0,83 Z" fill="#ffffff" />
          </svg>
        </span>
        <Link to="/web" className="gd-header-v2__logo">
          {/* Full wordmark on desktop/tablet; compact round GD badge on phones. */}
          <picture>
            <source media="(max-width: 576px)" srcSet="/iceholidays-assets/images/GD_logo-mobile-v2.png" />
            <img src="/iceholidays-assets/images/GD_logo.png" alt="Golden Destinations" />
          </picture>
        </Link>

        <nav className="gd-header-v2__nav">
          {NAV_ITEMS.map((item) => {
            const menu: MenuProps["items"] = (item.children ?? []).map((c, i) => ({
              key: i,
              label: <Link to={c.href}>{c.label}</Link>,
            }));
            return (
              <Dropdown key={item.label} menu={{ items: menu }} placement="bottom">
                <span className="gd-header-v2__nav-item">
                  {item.label}
                  <Icon path={mdiMenuDown} size="18px" />
                </span>
              </Dropdown>
            );
          })}
        </nav>

        {displayUsername ? (
          <Dropdown menu={{ items: userMenu }} placement="bottom">
            <span className="gd-header-v2__signin">
              hi, {truncatedName}
              <Icon path={mdiMenuDown} size="18px" />
            </span>
          </Dropdown>
        ) : (
          <Link to={SIGNIN_URL} className="gd-header-v2__signin">
            Sign In
          </Link>
        )}

        <button
          type="button"
          className="gd-header-v2__hamburger"
          aria-label="Open menu"
          onClick={() => setMenuOpen(true)}
        >
          <Icon path={mdiMenu} size="26px" />
        </button>
      </div>

      <Drawer
        rootClassName="gd-header-v2__drawer"
        placement="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        closeIcon={<Icon path={mdiClose} size="24px" />}
        title={
          <img
            className="gd-header-v2__drawer-logo"
            src="/iceholidays-assets/images/GD_logo.png"
            alt="Golden Destinations"
          />
        }
        width={288}
      >
        <Collapse ghost items={drawerItems} expandIconPosition="end" />
      </Drawer>
    </header>
  );
};

export default Header;
